import styled from "@emotion/styled";
import {Button, Dialog, DialogBody, DialogFooter, Text} from "@blueprintjs/core";
import React from "react";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {JobLinksRenderer} from "./JobLinksRenderer";
import "../manifest/details/components/AssignJobsConfirmation.css";

const DialogBox = styled(Dialog)`
  box-sizing: border-box;
  background: #ffdee0;
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  width: 390px;
  .bp5-dialog-header {
    background: #ffdee0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  padding-left: 13px;
  padding-top: 13px;
`;

const Title = styled(Text)`
  font-family: "Roboto", serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  padding-left: 19px;
  color: #161616;
`;

const HeaderIcon = styled(FontAwesomeIcon)``;

const Body = styled(DialogBody)`
  background: transparent;
  font-family: "Roboto", serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18.51px;
  padding: 9px 28px 14px;
`;

const BodyText = styled(Text)`
  color: #161616;
  &.body-text-bottom {
    padding-right: 4px;
  }
`;

const BodyTextCenter = styled(BodyText)`
  padding: 24px 0;
`;

const TextDetail = styled.div`
  padding-bottom: 4px;
  &:last-child {
    padding-bottom: 0;
  }
`;

const Footer = styled(DialogFooter)`
  background: transparent;
  border-radius: 0 0 0 0;
  border-top: 0;
  padding: 0 28px 18px;
`;

const CloseButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  background: #ffffff !important;
  box-shadow: 0px 2px 2px 0px #00000040 !important;
  border-radius: 4px;
  border: none;
  outline: none;
  font-family: "Roboto", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  &.button-no {
    color: #161616;
  }
  &:hover {
    background: rgba(143, 153, 168, 0.15) !important;
  }
`;

export interface EverCompletedCheckDialogProps {
  testIdPrefix: string;
  isVisible: boolean;
  everCompletedJobs: Array<{jobId: number; jobNumber: string; orderId?: number}>;
  actionText?: string;
  handleClose(): void;
}

const TITLE_MAP: Record<string, string> = {
  assigned: "Assignment Blocked",
  unassigned: "Unassignment Blocked",
  reassigned: "Reassignment Blocked"
};

export const EverCompletedCheckDialog = ({
  testIdPrefix,
  isVisible,
  everCompletedJobs,
  actionText = "assigned",
  handleClose
}: EverCompletedCheckDialogProps) => {
  const title = (
    <Header>
      <HeaderIcon data-testid={"header-icon"} color={"#FA545E"} size={"xl"} icon={solid("triangle-exclamation")} />
      <Title data-testid={"header-title"}>{TITLE_MAP[actionText] ?? "Assignment Blocked"}</Title>
    </Header>
  );

  const actions = (
    <CloseButton
      className="button-no"
      data-testid={`${testIdPrefix}-dialog-close-button`}
      text={"Close"}
      onClick={handleClose}
    />
  );

  return (
    <DialogBox
      data-testid={`${testIdPrefix}-dialog-box`}
      title={title}
      isOpen={isVisible}
      onClose={handleClose}
      className="assign-jobs-confirmation-dialog blocked"
      canOutsideClickClose={true}
      canEscapeKeyClose={true}
    >
      <Body>
        <BodyText>
          The job(s) was at one point completed. Based on company settings, it can&apos;t be {actionText}.
        </BodyText>
        <BodyTextCenter>
          {everCompletedJobs.map((job) => (
            <TextDetail key={job.jobId} data-testid={`${testIdPrefix}-job-${job.jobId}`}>
              Job {JobLinksRenderer([{jobNumber: job.jobNumber, jobId: job.jobId, orderId: job.orderId || 0}])}
            </TextDetail>
          ))}
        </BodyTextCenter>
      </Body>
      <Footer actions={actions}></Footer>
    </DialogBox>
  );
};
